import React from "react";
import moment from "moment";

import "./JobSwmsFormStyle.css";

const STATUS_LABELS = {
  in_progress: "In Progress",
  superseded: "Superseded",
  approved: "Approved",
};

const REVIEWER_STATUS_LABELS = {
  approved: "Approved",
  rejected: "Rejected",
  pending: "Pending",
  no_response: "No Response",
};

const getVersionBadgeClass = (status) => {
  if (status === "in_progress") return "swms-version-badge swms-version-badge-current";
  if (status === "approved") return "swms-version-badge swms-version-badge-approved";
  return "swms-version-badge swms-version-badge-superseded";
};

const getVersionStatusPillClass = (status) => {
  if (status === "in_progress") return "swms-version-status-pill swms-version-status-in-progress";
  if (status === "approved") return "swms-version-status-pill swms-version-status-approved";
  return "swms-version-status-pill swms-version-status-superseded";
};

const getReviewerBadgeClass = (status) => {
  if (status === "approved") return "swms-reviewer-badge swms-reviewer-badge-approved";
  if (status === "rejected") return "swms-reviewer-badge swms-reviewer-badge-rejected";
  if (status === "pending") return "swms-reviewer-badge swms-reviewer-badge-pending";
  return "swms-reviewer-badge swms-reviewer-badge-no-response";
};

const getVersionLabelNumber = (version, fallbackIndex) => {
  const versionLabel = version && version.version_number;
  const matchedNumber = typeof versionLabel === "string" && versionLabel.match(/(\d+)/);

  if (matchedNumber) {
    return matchedNumber[1];
  }

  return fallbackIndex + 1;
};

const getEditText = (version) => version.edit_text || version.edit_note || version.amendment_note;

const getInitials = (name) => {
  if (!name) return "??";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const VersionHistorySection = ({ versions = [], loading }) => {
  if (!versions || versions.length === 0) {
    return (
      <div className="my-2" style={{ color: "#94a3b8", fontSize: "13px", textAlign: "center", padding: "16px 0" }}>
        No version history available.
      </div>
    );
  }

  return (
    <div className="my-2">
      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-12">
            <div className="swms-version-timeline">
              {versions.map((version, vIndex) => (
                <div key={vIndex} className="swms-version-entry">
                  {/* Version header row */}
                  <div className="swms-version-header-row">
                    <div className="swms-version-badge-col">
                      <span className={getVersionBadgeClass(version.status)}>
                        {version.version_number || `v${vIndex + 1}`}
                      </span>
                      {vIndex < versions.length - 1 && (
                        <div className="swms-version-timeline-line mx-auto" />
                      )}
                    </div>
                    <div className="swms-version-header-content">
                      <div className="swms-version-meta-row">
                        <span className="swms-version-number-label">
                          Version {getVersionLabelNumber(version, vIndex)}
                        </span>
                        {version.swms_id && (
                          <span className="swms-version-id-tag">{version.swms_id}</span>
                        )}
                        {version.created_at && (
                          <span className="swms-version-created-label">
                            Created {moment.utc(version.created_at).format("DD/MM/YYYY")}
                            {version.created_by ? ` by ${version.created_by}` : ""}
                          </span>
                        )}
                        <span className={getVersionStatusPillClass(version.status)}>
                          {STATUS_LABELS[version.status] || version.status || ""}
                        </span>
                      </div>

                      {/* Edit note */}
                      {getEditText(version) && (
                        <div className="swms-amendment-note">
                          <span className="swms-amendment-note-label">Edit</span>
                          <span className="swms-amendment-note-text">{getEditText(version)}</span>
                        </div>
                      )}

                      {/* Submissions */}
                      {version.submissions && version.submissions.length > 0 && (
                        <div>
                          {version.submissions.map((submission, sIndex) => (
                            <div key={sIndex} className="swms-submission-block">
                              {/* Submission header */}
                              <div className="swms-submission-header">
                                <span className="swms-submission-label">
                                  Submission {sIndex + 1}
                                </span>
                                {submission.sent_at && (
                                  <span className="swms-submission-meta">
                                    Sent {moment.utc(submission.sent_at).format("DD/MM/YYYY")}
                                    {submission.reviewer_count
                                      ? ` · ${submission.reviewer_count} reviewer${submission.reviewer_count !== 1 ? "s" : ""}`
                                      : ""}
                                  </span>
                                )}
                              </div>

                              {/* Reviewer rows */}
                              {submission.reviewers && submission.reviewers.length > 0 && (
                                <div>
                                  {submission.reviewers.map((reviewer, rIndex) => (
                                    <div key={rIndex}>
                                      <div className="swms-reviewer-row">
                                        <div className="swms-reviewer-avatar">
                                          {getInitials(reviewer.name)}
                                        </div>
                                        <div className="swms-reviewer-info">
                                          <span className="swms-reviewer-name">{reviewer.name || ""}</span>
                                          {reviewer.position && (
                                            <span className="swms-reviewer-position">
                                              {" "}{reviewer.position}
                                            </span>
                                          )}
                                          {reviewer.responded_at && (
                                            <div className="swms-reviewer-responded">
                                              Responded {moment.utc(reviewer.responded_at).format("DD/MM/YYYY")}
                                            </div>
                                          )}
                                          {reviewer.status === "pending" && !reviewer.responded_at && (
                                            <div className="swms-reviewer-responded" style={{ color: "#94a3b8" }}>
                                              Link sent · awaiting response
                                            </div>
                                          )}
                                          {reviewer.status === "no_response" && (
                                            <div className="swms-reviewer-responded" style={{ color: "#cbd5e1" }}>
                                              No response before amendment
                                            </div>
                                          )}
                                        </div>
                                        <div className="swms-reviewer-actions">
                                          <span className={getReviewerBadgeClass(reviewer.status)}>
                                            {REVIEWER_STATUS_LABELS[reviewer.status] || reviewer.status || ""}
                                          </span>
                                          {reviewer.status === "pending" && (
                                            <button
                                              className="swms-auth-resend-btn"
                                              disabled={loading}
                                              type="button"
                                              style={{ marginLeft: "6px" }}
                                            >
                                              Resend
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {/* Rejection notes */}
                                      {reviewer.status === "rejected" && reviewer.rejection_notes && (
                                        <div className="swms-reviewer-rejection-note">
                                          <span className="swms-reviewer-rejection-label">Rejection Notes</span>
                                          <div className="swms-reviewer-rejection-text">
                                            {reviewer.rejection_notes}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionHistorySection;
